import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { filter } from 'rxjs';

import { LayoutService } from '../layout.service';
import { MenuItem } from '../models/menu.model';

@Component({
  selector: '[app-menuitem]',
  standalone: true,
  imports: [NgClass, RouterLink, RouterLinkActive, BadgeModule, TranslatePipe],
  host: {
    '[class.layout-root-menuitem]': 'root()',
    '[class.active-menuitem]': '!root() && active()',
  },
  animations: [
    trigger('children', [
      state('collapsed', style({ height: '0' })),
      state('expanded', style({ height: '*' })),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
  template: `
    <!-- ROOT group items -->
    @if (root() && item().items) {
      @if (item().label) {
        <div class="layout-menuitem-root-text">
          <span>{{ item().label | translate }}</span>
          <i class="layout-menuitem-root-icon pi pi-fw pi-ellipsis-h"></i>
        </div>
      }
      <ul>
        @for (child of item().items; track child.label; let i = $index) {
          <li app-menuitem [item]="child" [index]="i" [parentKey]="key()"></li>
        }
      </ul>
    }

    <!-- LEAF items (routerLink, no sub-items) -->
    @if (!root() && !item().items && item().routerLink) {
      <a
        [routerLink]="item().routerLink"
        [routerLinkActiveOptions]="item().routerLinkActiveOptions ?? { exact: false }"
        routerLinkActive="active-route"
        (click)="onLeafClick()"
      >
        @if (item().icon) {
          <i [ngClass]="item().icon" class="layout-menuitem-icon"></i>
        }
        <span>{{ item().label | translate }}</span>
        @if (item().badge && item().badge!() > 0) {
          <p-badge
            [value]="item().badge!().toString()"
            [severity]="item().badgeSeverity ?? 'info'"
            styleClass="ml-auto"
          />
        }
      </a>
    }

    <!-- BRANCH items (sub-items, not root — nested submenus) -->
    @if (!root() && item().items) {
      <a tabindex="0" role="button" (click)="toggleSubmenu()" (keydown.enter)="toggleSubmenu()">
        @if (item().icon) {
          <i [ngClass]="item().icon" class="layout-menuitem-icon"></i>
        }
        <span>{{ item().label | translate }}</span>
        <i class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
      </a>
      <ul [@children]="active() ? 'expanded' : 'collapsed'">
        @for (child of item().items; track child.label; let i = $index) {
          <li app-menuitem [item]="child" [index]="i" [parentKey]="key()"></li>
        }
      </ul>
    }
  `,
})
export class AppMenuitemComponent {
  readonly item = input.required<MenuItem>();
  readonly index = input(0);
  readonly root = input(false);
  readonly parentKey = input('');

  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);

  readonly active = signal(false);
  readonly key = signal('');

  constructor() {
    // Compute key from parentKey + index
    effect(() => {
      const parent = this.parentKey();
      const idx = this.index();
      this.key.set(parent ? `${parent}-${idx}` : `${idx}`);
    });

    // Auto-expand groups that contain the active route, or restore persisted state
    effect(() => {
      const menuItem = this.item();
      const currentKey = this.key();
      if (menuItem.items && currentKey) {
        const isChildActive = this.hasActiveChild(menuItem.items, this.router.url);
        if (isChildActive) {
          this.active.set(true);
          this.layoutService.setMenuActive(currentKey, true);
        } else {
          // Restore persisted state if no active child
          const persisted = this.layoutService.isMenuActive(currentKey);
          this.active.set(persisted);
        }
      }
    });

    // Listen for route changes to update active state
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        if (this.item().items) {
          this.updateActiveState((event as NavigationEnd).urlAfterRedirects);
        }
      });

    // Accordion: collapse this item when a non-ancestor/non-descendant item is activated
    this.layoutService.menuSource$.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (
        event.key !== this.key() &&
        !event.key.startsWith(this.key() + '-') &&
        !this.key().startsWith(event.key)
      ) {
        this.active.set(false);
      }
    });
  }

  toggleSubmenu(): void {
    this.active.update((v) => !v);
    this.layoutService.setMenuActive(this.key(), this.active());
    this.layoutService.onMenuStateChange({ key: this.key() });
  }

  onLeafClick(): void {
    this.layoutService.closeMobileMenu();
    this.layoutService.onMenuStateChange({ key: this.key() });
  }

  private updateActiveState(url: string): void {
    const menuItem = this.item();
    if (menuItem.items) {
      const isChildActive = this.hasActiveChild(menuItem.items, url);
      if (isChildActive) {
        this.active.set(true);
        this.layoutService.setMenuActive(this.key(), true);
        this.layoutService.onMenuStateChange({ key: this.key(), routeEvent: true });
      }
      // Don't collapse on navigation — preserve user's expanded state
    }
  }

  private hasActiveChild(items: MenuItem[], url: string): boolean {
    return items.some((child) => {
      if (child.routerLink) {
        const opts = child.routerLinkActiveOptions;
        if (opts?.exact) {
          return url === child.routerLink;
        }
        return url.startsWith(child.routerLink);
      }
      if (child.items) {
        return this.hasActiveChild(child.items, url);
      }
      return false;
    });
  }
}
